import React, { useEffect, useState } from "react";
import { CModal, CModalBody } from "@coreui/react";
import { toast } from "react-toastify";
import { ApiURLs } from "../../../constant/ApiUrls";
import { fetchData, postData, updateData, deleteData } from "../../../services/api";

/*
  Dealerships under one dealer account - the "+ Add dealership" screen.

  Chris: "just have them as one main dealer, then each of the sub dealers have
  their own address and contact details."

  A business had exactly one address, on its own row. Bright Motor Group runs
  eighteen franchises off three forecourts, so seventeen of their addresses and
  eight of their nine phone numbers had nowhere to go. This is where they go.

  Deliberately self-contained: it renders its own button and its own modal, the
  way AddDealer does, so the 1,437-line dealer form did not have to be opened up
  to add it.

  No @mui/icons-material here - it is not installed in this panel.
*/

const BLANK = {
  name: "", address: "", town: "", county: "", eircode: "",
  phone: "", email: "", website: "", status: 1,
};

const field = {
  width: "100%", padding: "7px 9px", border: "1px solid #d5d8dd",
  borderRadius: 4, fontSize: 13, marginTop: 3,
};
const label = { fontSize: 12, color: "#5b6472", fontWeight: 600 };
const cell = { padding: "7px 8px", fontSize: 12, borderBottom: "1px solid #eef0f3", verticalAlign: "top" };

const DealerBranches = ({ dealerData }) => {
  const userId = dealerData?.id;
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [counties, setCounties] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!userId) return;
    try {
      const res = await fetchData(`${ApiURLs.DEALER_BRANCHES}?user_id=${userId}`);
      setRows(res?.data?.branches || []);
    } catch (e) {
      toast.error("Could not load the dealerships");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    /* The county has to become the id the search filter uses. Typing "Co. Dublin"
       into a text box is how a dealership ends up in a county nothing can find,
       so this is a dropdown of the real list. */
    fetchData(ApiURLs.GET_LOCATIONS)
      .then((r) => setCounties(r?.data?.location_data || []))
      .catch(() => setCounties([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "", address: row.address || "", town: row.town || "",
      county: row.county || "", eircode: row.eircode || "", phone: row.phone || "",
      email: row.email || "", website: row.website || "", status: row.status,
    });
  };

  const cancel = () => { setEditingId(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("A dealership needs a name"); return; }
    setBusy(true);
    try {
      if (editingId) {
        /* Unlike postData, updateData and deleteData say nothing on success -
           checked, not assumed - so the confirmation is raised here. */
        await updateData(form, `${ApiURLs.DEALER_BRANCHES}/${editingId}`);
        toast.success("Dealership updated");
      } else {
        await postData({ ...form, user_id: userId }, ApiURLs.DEALER_BRANCHES);
      }
      cancel();
      await load();
    } catch (e) {
      /* handleErrors in the service layer has already shown what went wrong. */
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    /* Say what happens to the cars, because "delete" on a thing that owns stock
       is the kind of button people press once and regret. */
    const n = Number(row.live_ads || 0);
    const warn = n
      ? `Remove ${row.name}?\n\n${n} live advert${n === 1 ? "" : "s"} will stay on the site and go back to the main dealer account.`
      : `Remove ${row.name}?`;
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      await deleteData(`${ApiURLs.DEALER_BRANCHES}/${row.id}`);
      toast.success(n ? `Dealership removed, ${n} advert${n === 1 ? "" : "s"} kept` : "Dealership removed");
      await load();
    } catch (e) {
      /* handleErrors in the service layer has already shown what went wrong. */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Dealerships under this account"
        style={{
          backgroundColor: "#1f5fbf", color: "white", border: "none", cursor: "pointer",
          borderRadius: 4, fontSize: 10, width: "100%", height: 24, padding: "2px 5px",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        Dealerships
      </button>

      <CModal visible={open} onClose={() => { cancel(); setOpen(false); }} size="xl" alignment="center">
        <CModalBody style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h5 style={{ margin: 0 }}>Dealerships - {dealerData?.name || "dealer"}</h5>
            <button onClick={() => { cancel(); setOpen(false); }}
              style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer" }}>x</button>
          </div>
          <p style={{ fontSize: 12, color: "#5b6472", marginTop: 6 }}>
            One account, several forecourts. Each dealership carries its own address and
            phone number, and adverts tagged to it show that address instead of head office.
          </p>

          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
            <thead>
              <tr style={{ background: "#f6f7f9" }}>
                <th style={{ ...cell, fontWeight: 700 }}>Name</th>
                <th style={{ ...cell, fontWeight: 700 }}>Address</th>
                <th style={{ ...cell, fontWeight: 700 }}>Town</th>
                <th style={{ ...cell, fontWeight: 700 }}>County</th>
                <th style={{ ...cell, fontWeight: 700 }}>Eircode</th>
                <th style={{ ...cell, fontWeight: 700 }}>Phone</th>
                <th style={{ ...cell, fontWeight: 700 }}>Live ads</th>
                <th style={{ ...cell, fontWeight: 700 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ opacity: r.status === 1 ? 1 : 0.5 }}>
                  <td style={cell}>{r.name}{r.status === 1 ? "" : " (hidden)"}</td>
                  <td style={cell}>{r.address || "-"}</td>
                  <td style={cell}>{r.town || "-"}</td>
                  <td style={cell}>{r.county || "-"}</td>
                  <td style={cell}>{r.eircode || "-"}</td>
                  <td style={cell}>{r.phone || "-"}</td>
                  <td style={cell}>{r.live_ads}</td>
                  <td style={cell}>
                    <button onClick={() => startEdit(r)} disabled={busy}
                      style={{ marginRight: 6, fontSize: 11, cursor: "pointer" }}>Edit</button>
                    <button onClick={() => remove(r)} disabled={busy}
                      style={{ fontSize: 11, cursor: "pointer", color: "#b3261e" }}>Remove</button>
                  </td>
                </tr>
              ))}
              {loaded && rows.length === 0 && (
                <tr><td style={{ ...cell, color: "#7a828e" }} colSpan={8}>
                  No dealerships yet. This account shows its own address on every advert.
                </td></tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: 16, borderTop: "1px solid #e6e8eb", paddingTop: 12 }}>
            <strong style={{ fontSize: 14 }}>{editingId ? "Edit dealership" : "+ Add dealership"}</strong>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 8 }}>
              <div><div style={label}>Name</div>
                <input style={field} value={form.name} onChange={set("name")} placeholder="Bright Ford Navan Road" /></div>
              <div style={{ gridColumn: "span 2" }}><div style={label}>Address</div>
                <input style={field} value={form.address} onChange={set("address")} placeholder="Mill Lane, Ashtown Grove" /></div>
              <div><div style={label}>Town</div>
                <input style={field} value={form.town} onChange={set("town")} placeholder="Dublin 15" /></div>
              <div><div style={label}>County</div>
                <select style={field} value={form.county} onChange={set("county")}>
                  <option value="">Select a county</option>
                  {counties.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select></div>
              <div><div style={label}>Eircode</div>
                <input style={field} value={form.eircode} onChange={set("eircode")} placeholder="D15 X336" /></div>
              <div><div style={label}>Phone</div>
                <input style={field} value={form.phone} onChange={set("phone")} placeholder="01 868 9000" /></div>
              <div><div style={label}>Email</div>
                <input style={field} value={form.email} onChange={set("email")} placeholder="optional" /></div>
              <div style={{ gridColumn: "span 2" }}><div style={label}>Website</div>
                <input style={field} value={form.website} onChange={set("website")} placeholder="optional" /></div>
              <div><div style={label}>Shown on the site</div>
                <select style={field} value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}>
                  <option value={1}>Yes</option>
                  <option value={0}>No, hide it</option>
                </select></div>
            </div>
            <div style={{ marginTop: 12 }}>
              <button onClick={save} disabled={busy}
                style={{ background: "#1f5fbf", color: "#fff", border: "none", borderRadius: 4, padding: "8px 16px", cursor: "pointer" }}>
                {busy ? "Saving..." : editingId ? "Save changes" : "Add dealership"}
              </button>
              {editingId && (
                <button onClick={cancel} disabled={busy}
                  style={{ marginLeft: 8, background: "#eceff3", border: "none", borderRadius: 4, padding: "8px 16px", cursor: "pointer" }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </CModalBody>
      </CModal>
    </>
  );
};

export default DealerBranches;
